import React, { useState } from 'react';
import {
  useDeleteTask,
  useGetTaskPriority,
  useGetTasks,
  useGetTaskType,
  useWriteTaskFullObject,
} from '../hooks/reactQuerys/TaskQueries';
import { useGetUserGroups, useGetUsers } from '../hooks/data';
import {
  Alert,
  CircularProgress,
  Typography,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Checkbox,
  ListItemText,
  Grid,
  Button,
  FormControlLabel,
  Switch,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  ITask,
  ITaskFullObject,
  taskFullObjectSchema,
} from '../Interfaces/ITask';
import { TaskPriorityChip } from './TaskPriorityChip';
import { TaskDeadlineChip } from './TaskDeadlineChip';
import { DialogTask } from './DialogTask';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, Delete } from '@mui/icons-material';
import { useAlert } from '../context/AlertContext';

export const TaskOverview: React.FC = () => {
  const { idTask } = useParams();
  const navigator = useNavigate();
  const { showAlert } = useAlert();

  const { deleteTask, isPendingDeleteTask } = useDeleteTask();
  const { writeTaskFullObject } = useWriteTaskFullObject();

  const { tasks, isLoadingTasks, refetechTasks } = useGetTasks();
  const { taskTypes, isLoadingTaskTypes } = useGetTaskType();
  const { taskPriorities, isLoadingTaskPriorities } = useGetTaskPriority();
  const { users, isLoadingUsers } = useGetUsers();
  const { userGroups, isLoadingUserGroups } = useGetUserGroups();

  const [showClosed, setShowClosed] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<number[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<number[]>([]);

  const [taskToDelete, setTaskToDelete] = useState<ITask | null>(null);

  const handleRemove = async () => {
    if (taskToDelete) {
      try {
        await deleteTask(taskToDelete.idTask);
        refetechTasks();
        showAlert({
          severity: 'success',
          text: 'Aufgabe erfolgreich gelöscht!',
        });
        setTaskToDelete(null);
      } catch (error) {
        console.error(error);
        showAlert({
          severity: 'error',
          text: 'Fehler beim Löschen der Aufgabe!',
        });
      }
    }
  };

  // #1194: Schnell-Erledigen direkt aus der Übersicht (jeder darf).
  const handleComplete = async (task: ITaskFullObject) => {
    try {
      await writeTaskFullObject(
        taskFullObjectSchema.parse({ ...task, Closed_at: new Date() })
      );
      refetechTasks();
      showAlert({
        severity: 'success',
        text: 'Aufgabe als erledigt markiert!',
      });
    } catch (error) {
      console.error(error);
      showAlert({
        severity: 'error',
        text: 'Fehler beim Erledigen der Aufgabe!',
      });
    }
  };

  // Loading abfangen
  if (
    isLoadingTasks ||
    isLoadingTaskTypes ||
    isLoadingTaskPriorities ||
    isLoadingUsers ||
    isLoadingUserGroups
  ) {
    return <CircularProgress />;
  }

  // Fehler abfangen
  if (!tasks || !taskTypes || !taskPriorities || !users || !userGroups) {
    return <Alert severity='error'>Fehler beim Laden der Daten!</Alert>;
  }

  // Arrays safe defaulten
  const safeUsers = users || [];
  const safeUserGroups = userGroups || [];
  const safeTaskTypes = taskTypes || [];
  const safeTaskPriorities = taskPriorities || [];

  // Gefilterte Tasks
  const filteredTasks = tasks
    .filter(task => (showClosed ? true : task.Closed_at === null))
    .filter(
      task =>
        selectedUsers.length === 0 ||
        selectedUsers.some(user => task.Users.includes(user))
    )
    .filter(
      task =>
        selectedTypes.length === 0 || selectedTypes.includes(task.idTaskType)
    )
    .filter(
      task =>
        selectedPriorities.length === 0 ||
        selectedPriorities.includes(task.idTaskPriority)
    );

  const columns: GridColDef<ITaskFullObject>[] = [
    { field: 'idTask', headerName: 'Nr.', width: 70 },
    {
      field: 'TaskType',
      headerName: 'Art',
      width: 120,
      renderCell: params =>
        safeTaskTypes.find(t => t.idTaskType === params.row.idTaskType)
          ?.TaskType,
    },
    {
      field: 'idTaskPriority',
      headerName: 'Priorität',
      width: 120,
      renderCell: params => <TaskPriorityChip task={params.row} />,
    },
    {
      field: 'Deadline_at',
      headerName: 'Frist',
      width: 120,
      renderCell: params => <TaskDeadlineChip task={params.row} />,
    },
    {
      field: 'Users',
      headerName: 'Mitarbeiter',
      width: 200,
      renderCell: params =>
        safeUsers
          .filter(u => params.row.Users.includes(u.idUser))
          .map(u => `${u.FirstName} ${u.LastName}`)
          .join(', '),
    },
    {
      field: 'UserGroup',
      headerName: 'Benutzergruppen',
      width: 200,
      renderCell: params =>
        safeUserGroups
          .filter(u => params.row.UserGroups.includes(u.idUserGroup))
          .map(u => `${u.UserGroup}`)
          .join(', '),
    },
    { field: 'Title', headerName: 'Titel', flex: 1 },
    {
      field: 'Deleted_at',
      headerName: 'Aktionen',
      width: 120,
      renderCell: params => (
        <>
          {params.row.Closed_at === null && (
            <IconButton
              title='Als erledigt markieren'
              onClick={event => {
                event.stopPropagation();
                handleComplete(params.row);
              }}
            >
              <Check />
            </IconButton>
          )}
          <IconButton
            onClick={event => {
              event.stopPropagation();
              setTaskToDelete(params.row);
            }}
          >
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  // Dropdown-Select mit Checkboxen
  const renderSelect = <T extends { [key: string]: any }>(
    label: string,
    items: T[],
    value: number[],
    setValue: React.Dispatch<React.SetStateAction<number[]>>,
    idField: keyof T,
    labelField: keyof T
  ) => (
    <FormControl fullWidth size='small'>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        fullWidth
        label={label}
        onChange={e =>
          setValue(
            typeof e.target.value === 'string'
              ? e.target.value.split(',').map(Number)
              : e.target.value
          )
        }
        renderValue={selected => {
          if (!selected || (selected as number[]).length === 0) {
            return <em>Kein Filter</em>;
          }
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as number[]).map(id => {
                const item = items.find(i => i[idField] === id);
                return item ? (
                  <Chip
                    key={id}
                    label={String(item[labelField])}
                    size='small'
                  />
                ) : null;
              })}
            </Box>
          );
        }}
      >
        {items.map(item => {
          const id = item[idField] as number;
          const lbl = String(item[labelField]);
          return (
            <MenuItem key={id} value={id}>
              <Checkbox checked={value.includes(id)} />
              <ListItemText primary={lbl} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      {taskToDelete && (
        <Dialog open onClose={() => setTaskToDelete(null)}>
          <DialogContent>
            Wollen Sie die Aufgabe <i>{taskToDelete.Title} wirklich löschen?</i>
          </DialogContent>
          <DialogActions>
            <Button
              variant='outlined'
              onClick={handleRemove}
              disabled={isPendingDeleteTask}
            >
              {isPendingDeleteTask ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <span>Löschen</span>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                'Löschen '
              )}
            </Button>

            <Button
              variant='contained'
              onClick={() => setTaskToDelete(null)}
              disabled={isPendingDeleteTask}
            >
              Abbruch
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {idTask && (
        <DialogTask
          idTask={Number(idTask)}
          setIdTask={() => navigator('/task')}
          refatchTasks={refetechTasks}
          taskPriority={taskPriorities}
          taskTypes={taskTypes}
          userGroups={userGroups}
          users={users}
        />
      )}

      <Typography variant='h4' sx={{ mb: 2 }}>
        Aufgaben
        <FormControlLabel
          sx={{ ml: 2 }}
          control={
            <Switch
              checked={showClosed}
              onChange={e => setShowClosed(e.target.checked)}
            />
          }
          label='Erledigte Aufgaben'
        />
        <Button
          sx={{ float: 'right' }}
          variant='outlined'
          onClick={() => navigator('/task/-1')}
        >
          Neue Aufgabe
        </Button>
      </Typography>

      {/* DROPDOWN FILTERS */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          {renderSelect(
            'Art',
            safeTaskTypes,
            selectedTypes,
            setSelectedTypes,
            'idTaskType',
            'TaskType'
          )}
        </Grid>
        <Grid item xs={4}>
          {renderSelect(
            'Priorität',
            safeTaskPriorities,
            selectedPriorities,
            setSelectedPriorities,
            'idTaskPriority',
            'TaskPriority'
          )}
        </Grid>
        <Grid item xs={4}>
          {renderSelect(
            'Mitarbeiter',
            safeUsers,
            selectedUsers,
            setSelectedUsers,
            'idUser',
            'LastName'
          )}
        </Grid>
      </Grid>

      {/* DATA GRID */}
      <DataGrid
        rows={filteredTasks}
        columns={columns}
        getRowId={row => row.idTask}
        autoHeight
        pageSizeOptions={[5, 10, 25]}
        onRowClick={params => navigator('/task/' + params.id)}
      />
    </Box>
  );
};
